import React, { lazy, Suspense, useEffect, useState, Fragment } from 'react'
import { withRouter } from 'react-router-dom'
import { Layout, Spin, Row, Button } from 'antd'
import { useDispatch } from 'react-redux'
import { appSelector } from '../helpers/appSelector'
import { AppDispatch } from '../helpers/appDispatch'
import {
  getMerchantsOverview,
  exportOverviewRequest
} from '../store/merchant-overview'
import { isEmpty } from '../helpers/isEmpty'
import {
  MerchantOverview,
  MerchantData,
  MerchantOverviewReport
} from '../interfaces'
import { getMerchantsRequest } from '../store/reports'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

const Filters = lazy(() => import('../components/merchants-overview/Filters'))
const Cards = lazy(() => import('../components/merchants-overview/Cards'))
const Details = lazy(() => import('../components/merchants-overview/Details'))
const CurrencyFilter = lazy(
  () => import('../components/merchants-overview/CurrencyFilter')
)
const EmptyBox = lazy(() => import('../components/merchants-overview/EmptyBox'))

const { Content } = Layout

const MerchantsOverview = () => {
  const dispatch: AppDispatch = useDispatch()
  const { t } = useTranslation()
  const overviewsStore = appSelector((state) => state.overviews)
  const reports = appSelector((state) => state.reports)
  const [overviewReport, setOverviewReport] =
    useState<MerchantOverviewReport | null>(null)
  const [merchants, setMerchants] = useState<MerchantData[]>(reports.merchants)
  const [exportType, setExportType] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [overviewdata, setOverviewdata] = useState<MerchantOverview[]>([])
  const [merchant, setMerchant] = useState<MerchantData | null>(null)
  const [currency, setCurrency] = useState('USD')
  const [loading, setLoading] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [pageSize, setPageSize] = useState(10)
  const [skip, setSkip] = useState(0)
  const [isSearching, setIsSearching] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  const getSearchParams = () => ({
    currency,
    periodFrom: fromDate,
    periodTo: toDate,
    merchant: merchant?.name || '',
    exportType,
    pageSize,
    skip,
    fixedPeriod: 'overall'
  })

  useEffect(() => {
    const { merchants } = reports
    if (isEmpty(merchants)) {
      dispatch(getMerchantsRequest())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const { loading, overviews, isExporting } = overviewsStore
    const { merchants } = reports
    setLoading(loading)
    setOverviewdata(overviews && !isEmpty(overviews.data) ? overviews.data : [])
    setOverviewReport(overviews)
    setMerchants(merchants)
    setIsExporting(isExporting)
  }, [overviewsStore, reports])

  const reloadOverviews = () => {
    const searchParams = getSearchParams()
    dispatch(getMerchantsOverview(searchParams))
  }

  const onExportClick = (type: string) => {
    setExportType(type)
    const searchParams = getSearchParams()
    searchParams.exportType = type
    dispatch(exportOverviewRequest(searchParams))
  }

  const onLoadMore = (page: number, size: number) => {
    const newSkip = page - 1
    setPageSize(size)
    setSkip(newSkip)
    setCurrentPage(page)
    
    const searchParams = getSearchParams()
    searchParams.skip = newSkip
    searchParams.pageSize = size
    
    dispatch(getMerchantsOverview(searchParams))
  }

  const onReset = (form: any) => {
    form.resetFields()
    setFromDate('')
    setToDate('')
    setMerchant(null)
    setSkip(0)
    setCurrentPage(1)
    setIsSearching(false)
    
    const resetParams = {
      currency,
      periodFrom: '',
      periodTo: '',
      merchant: '',
      exportType: '',
      pageSize: 10,
      skip: 0,
      fixedPeriod: 'overall'
    }
    
    dispatch(getMerchantsOverview(resetParams))
  }

  const onSelectCurrency = (value: string) => {
    setCurrency(value)
    setSkip(0)
    setCurrentPage(1)
    
    const searchParams = getSearchParams()
    searchParams.currency = value
    searchParams.skip = 0
    
    dispatch(getMerchantsOverview(searchParams))
  }

  const onSearch = (values: any) => {
    setIsSearching(true)
    setSkip(0)
    setCurrentPage(1)
    
    const { periodFrom, periodTo, merchant: merchantId } = values
    let m: MerchantData | undefined = undefined
    let pFrom: string = '',
      pTo: string = ''
      
    if (periodFrom !== undefined && periodTo !== undefined) {
      pFrom = moment(periodFrom).format('MM/DD/YYYY')
      pTo = moment(periodTo).format('MM/DD/YYYY')
      setFromDate(pFrom)
      setToDate(pTo)
    }
    
    if (merchantId !== undefined) {
      m = merchants.find((m) => m.merchantId === merchantId)
      setMerchant(m !== undefined ? m : null)
    } else {
      setMerchant(null)
    }

    const searchParams = {
      currency,
      periodFrom: pFrom,
      periodTo: pTo,
      merchant: m?.name || '',
      exportType: '',
      pageSize,
      skip: 0,
      fixedPeriod: 'overall'
    }
    
    dispatch(getMerchantsOverview(searchParams))
  }

  return (
    <div className="padding-box">
      <Content className="site-layout-background site-box">
        <Suspense
          fallback={
            <Row className="suspense-container">
              <div style={{ marginTop: '200px' }}>
                <Spin />
              </div>
            </Row>
          }
        >
          <Fragment>
            <Filters
              onReset={onReset}
              onSearch={onSearch}
              merchants={merchants}
              translate={t}
            />
            {isSearching ? (
              <Fragment>
                <Cards
                  overviews={overviewReport}
                  currency={currency}
                  loading={loading}
                  translate={t}
                />
                <div className="margin-top">
                  <Row style={{ position: 'relative' }}>
                    <h4 className="transaction-chart-text">
                      {t('general.merchantsTable')}
                    </h4>
                    <div className="utility-buttons">
                      <>
                        <Button
                          type="primary"
                          className="export-buttons"
                          onClick={() => onExportClick('EXCEL')}
                          loading={isExporting && exportType === 'EXCEL'}
                          disabled={isEmpty(overviewdata)}
                        >
                          {t('general.export-excel')}
                        </Button>
                      </>
                      <Button
                        type="primary"
                        className="export-buttons reload"
                        onClick={() => reloadOverviews()}
                      >
                        {t('general.refresh')}
                      </Button>
                    </div>
                  </Row>
                  <CurrencyFilter
                    onSelectCurrency={onSelectCurrency}
                    translate={t}
                  />
                  <Details
                    overviews={overviewdata}
                    currency={currency}
                    loading={loading}
                    onLoadMore={onLoadMore}
                    total={overviewReport ? overviewReport.totalMerchants.value : 0}
                    translate={t}
                    currentPage={currentPage}
                  />
                </div>
              </Fragment>
            ) : (
              <EmptyBox header={t('general.filterToGetData')} description="" />
            )}
          </Fragment>
        </Suspense>
      </Content>
    </div>
  )
}

export default withRouter(MerchantsOverview)
